<script setup lang="ts">
/**
 * The Z-report, on a laptop.
 *
 * Every term of the drawer expectation is on screen, because a variance
 * conversation with a member of staff has to be about arithmetic rather than
 * about trust. "The till says you are 240 Kč short" is an accusation; "opening
 * float 2 000, cash sales 8 340, paid out 1 200, dropped 5 000, expected 4 140,
 * counted 3 900" is a question two people can answer together.
 *
 * The cash figures are folded from the same `tender.recorded` events the server
 * folds — not counted a second way. A second accumulator agrees on every
 * ordinary evening and disagrees on exactly the one somebody asks about.
 */

import { computed, ref } from "vue";
import type { ShiftPage, ShiftReport } from "../api";
import { businessDate, clockTime, money, signedMoney } from "../format";

const props = defineProps<{
  shifts: readonly ShiftReport[];
  unattributed: ShiftPage["unattributed"];
  timezone: string;
  locale: string;
  t: (key: string) => string;
}>();

const expanded = ref<string | null>(null);

const toggle = (shiftId: string): void => {
  expanded.value = expanded.value === shiftId ? null : shiftId;
};

const TERMS = [
  "openingFloatMinor",
  "cashSalesMinor",
  "cashRefundsMinor",
  "roundingMinor",
  "cashTipsMinor",
  "payInsMinor",
  "payOutsMinor",
  "safeDropsMinor",
  "tipOutsMinor",
] as const;

/** Direction and severity, each as a word. Never a colour on its own. */
const directionWord = (shift: ShiftReport): string => {
  if (!shift.variance) return props.t("shifts.uncounted");
  if (shift.variance.direction === "OVER") return props.t("shifts.over");
  if (shift.variance.direction === "SHORT") return props.t("shifts.short");
  return props.t("shifts.exact");
};

const severityWord = (shift: ShiftReport): string => {
  if (!shift.variance) return "";
  if (shift.variance.severity === "ALERT") return props.t("shifts.alert");
  if (shift.variance.severity === "WATCH") return props.t("shifts.watch");
  return props.t("shifts.ok");
};

const tone = (shift: ShiftReport): string => {
  if (!shift.variance) return "";
  if (shift.variance.severity === "ALERT") return "gf-chip--bad";
  if (shift.variance.severity === "WATCH") return "gf-chip--warn";
  return "";
};

const anyOver = computed(() =>
  props.shifts.some((s) => s.variance?.direction === "OVER" && s.variance.severity !== "OK"),
);
</script>

<template>
  <section>
    <p v-if="anyOver" class="gf-note gf-note--warn">{{ t("shifts.overIsNotSafe") }}</p>

    <!--
      CASH THAT IS IN NO Z-REPORT.

      A cash tender rung with no drawer open belongs to no shift, so it appears
      in none of the rows below and balances against nothing. Left off the
      screen it is money nobody was ever told about — the only kind that goes
      missing without anybody noticing. It is rendered even when the count is
      small, and it disappears entirely when it is zero.
    -->
    <p v-if="unattributed.tenders > 0" class="gf-note gf-note--warn">
      {{ t("shifts.unattributed") }} — {{ money(unattributed.cashMinor, locale) }}
      ({{ unattributed.tenders }})
      <template v-if="unattributed.ambiguous > 0">
        · {{ t("shifts.ambiguous") }} ({{ unattributed.ambiguous }})
      </template>
    </p>

    <div class="gf-panel">
      <div class="gf-scroll">
        <table class="gf-table">
          <thead>
            <tr>
              <th scope="col">{{ t("common.date") }}</th>
              <th scope="col">{{ t("shifts.cashier") }}</th>
              <th scope="col">{{ t("checks.opened") }}</th>
              <th scope="col" class="gf-num">{{ t("shifts.expected") }}</th>
              <th scope="col" class="gf-num">{{ t("shifts.counted") }}</th>
              <th scope="col" class="gf-num">{{ t("shifts.variance") }}</th>
              <th scope="col"><span class="gf-sr">{{ t("shifts.breakdown") }}</span></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="shift in shifts" :key="shift.shiftId">
              <tr>
                <th scope="row">{{ businessDate(shift.businessDate, locale) }}</th>
                <!--
                  The name, falling back to the id. A variance conversation
                  starts with a person, and "staff:eva" is not one.
                -->
                <td>{{ shift.cashierName ?? shift.cashierId }}</td>
                <td>
                  {{ clockTime(shift.openedAt, timezone, locale) }}
                  <span v-if="shift.state === 'OPEN'" class="gf-chip gf-chip--warn">
                    {{ t("shifts.open") }}
                  </span>
                </td>
                <td class="gf-num">{{ money(shift.expectedMinor, locale) }}</td>
                <td class="gf-num">
                  {{ shift.countedMinor === null ? "—" : money(shift.countedMinor, locale) }}
                </td>
                <td class="gf-num">
                  <template v-if="shift.variance">
                    <span class="gf-chip" :class="tone(shift)">
                      {{ directionWord(shift) }}
                      <template v-if="shift.variance.varianceMinor !== 0n">
                        {{ signedMoney(shift.variance.varianceMinor, locale) }}
                      </template>
                      · {{ severityWord(shift) }}
                    </span>
                  </template>
                  <span v-else class="gf-muted">{{ t("shifts.uncounted") }}</span>
                </td>
                <td>
                  <!-- One short word per row, loud only where the drawer needs looking into. -->
                  <button
                    type="button"
                    class="gf-link shifts__open"
                    :class="{ 'gf-link--quiet': tone(shift) === '' }"
                    :aria-label="t('shifts.breakdown')"
                    :aria-expanded="expanded === shift.shiftId"
                    @click="toggle(shift.shiftId)"
                  >
                    {{ t("shifts.breakdownShort") }}
                  </button>
                </td>
              </tr>
              <tr v-if="expanded === shift.shiftId">
                <td colspan="7" class="gf-expand">
                  <dl class="gf-terms gf-terms--figures">
                    <template v-for="term in TERMS" :key="term">
                      <dt>{{ t(`shifts.${term}`) }}</dt>
                      <dd class="gf-num">{{ money(shift.breakdown[term] ?? 0n, locale) }}</dd>
                    </template>
                    <dt class="gf-strong">{{ t("shifts.expected") }}</dt>
                    <dd class="gf-num gf-strong">{{ money(shift.expectedMinor, locale) }}</dd>
                  </dl>

                  <template v-if="shift.movements.length > 0">
                    <h3 class="gf-expand__sub">{{ t("shifts.movements") }}</h3>
                    <ul class="gf-list">
                      <li v-for="(m, i) in shift.movements" :key="i">
                        {{ m.kind }} · {{ m.reasonCode }} — {{ money(m.amountMinor, locale) }}
                      </li>
                    </ul>
                  </template>

                  <ul v-if="shift.anomalies.length > 0" class="gf-list gf-list--warn">
                    <li v-for="(a, i) in shift.anomalies" :key="i">{{ a.kind }} — {{ a.detail }}</li>
                  </ul>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p v-if="shifts.length === 0" class="gf-quiet">{{ t("common.empty") }}</p>
    </div>
  </section>
</template>

<style scoped>

.gf-expand {
  background: var(--gf-color-surface-sunken);
  padding: var(--gf-space-4) var(--gf-space-5);
}

/* 32 px of target: the word alone was 23 px tall. */
.shifts__open {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
}

.gf-expand__sub {
  margin: var(--gf-space-4) 0 var(--gf-space-2);
  font-size: var(--gf-text-base);
  font-weight: var(--gf-weight-semibold);
}
</style>
